/* eslint-disable no-undef */
import React from "react";

// material-ui components
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import Close from "@material-ui/icons/Close";
// core components

import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import styles from "assets/jss/material-dashboard-pro-react/modalStyle.js";
import { addDays, isAfter, isBefore, isEqual, parse } from "date-fns";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import { useWorkItemManualValidation } from "services/input-core/work-items/manual-validate";
import { checkMessageErrors } from "utils/functions";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { useUserInfo } from "store/auth/auth-store";
import { useClientConfig } from "hooks/useClientConfig";
import { Checkbox, FormControlLabel } from "@mui/material";

const useStyles = makeStyles(styles as any);

interface ManualValidationModalProps {
  open: any;
  OnSuccess: any;
  OnFail: any;
  workItemID: any;
  fieldglassId: any;
  setOpen: any;
  entityDate?: {
    start: string;
    end: string;
  };
  activityItemCode: string;
}

export default function ManualValidationModal({
  open,
  OnSuccess,
  OnFail,
  workItemID,
  fieldglassId,
  setOpen,
  entityDate,
  activityItemCode,
}: ManualValidationModalProps) {
  const { t } = useTranslation(["manual-validation-modal", "list-errors"]);
  const { userInfo } = useUserInfo();
  const { clientConfigSidebar } = useClientConfig();
  const [observation, setObservation] = React.useState("");
  const [expirationDate, setExpirationDate] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [checked, setChecked] = React.useState<boolean>(false);

  const handleChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const { mutate: submitManualValidation } = useWorkItemManualValidation();

  const isAnyFieldNull = () => {
    return !observation || (!expirationDate && !checked);
  };

  async function onSubmit() {
    if (observation === "" || (expirationDate === null && !checked)) {
      toast.error(
        t("fill-required-fields", { ns: "manual-validation-modal" }),
        {
          className: "error-toast-message",
        },
      );
      return;
    }

    setIsSubmitting(true);

    if (isAnyFieldNull()) {
      OnFail(t("field-error-message", { ns: "manual-validation-modal" }));
      setIsSubmitting(false);
      return;
    }

    submitManualValidation(
      {
        workItemID: workItemID,
        reason: observation,
        activityItemCode: activityItemCode,
        fieldglassId: fieldglassId,
        ...(checked
          ? {
              hasInfiniteLifetime: true,
            }
          : {
              validationExpirationDate: expirationDate,
            }),
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          setExpirationDate(null);
          setObservation("");
          toast.success(
            t("success-manual-validation", {
              ns: "manual-validation-modal",
            }),
            {
              className: "success-toast-message",
            },
          );
          setOpen(false);
          OnSuccess();
        },
        onError: (error: any) => {
          setIsSubmitting(false);
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_MANUAL_VALIDATION_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
          OnFail();
        },
      },
    );
  }

  const classes = useStyles();

  return (
    <Dialog
      classes={{
        root: classes.center,
        paper: classes.modal,
      }}
      open={open}
      keepMounted
      onClose={() => setOpen(false)}
      disableBackdropClick={true}
      aria-labelledby="modal-slide-title"
      aria-describedby="modal-slide-description"
    >
      <DialogTitle
        id="classic-modal-slide-title"
        disableTypography
        className={classes.modalHeader}
      >
        <ButtonTS
          justIcon
          className={classes.modalCloseButton}
          key="close"
          aria-label="Close"
          color="transparent"
          onClick={() => setOpen(false)}
        >
          <Close className={classes.modalClose} />
        </ButtonTS>
        <h4 className={classes.modalTitle}>
          {t("modal-title", { ns: "manual-validation-modal" })}
        </h4>
      </DialogTitle>
      <DialogContent id="modal-slide-description" className={classes.modalBody}>
        {isSubmitting ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            marginTop="16px"
            width="400px"
          >
            <CircularProgress size="48px" />
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            marginTop="16px"
            width="400px"
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                onChange={(d: any) => setExpirationDate(d)}
                value={expirationDate}
                closeOnSelect
                label={t("expiration-date", { ns: "manual-validation-modal" })}
                className="cy-datetime-picker"
                shouldDisableDate={(current) => {
                  const minDate = addDays(new Date(), 6);
                  const parsedStartDate = entityDate?.start
                    ? parse(entityDate.start, "dd/MM/yyyy", new Date())
                    : null;

                  if (parsedStartDate) {
                    if (isAfter(new Date(), parsedStartDate)) {
                      return isBefore(current, minDate);
                    } else if (isBefore(new Date(), parsedStartDate)) {
                      const adjustedMinDate = addDays(parsedStartDate, 6);
                      return isBefore(current, adjustedMinDate);
                    }
                  }

                  return isBefore(current, minDate);
                }}
                disabled={isSubmitting || checked}
              />
            </LocalizationProvider>
            {clientConfigSidebar?.hasApprovalWithoutExpiration && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={handleChangeCheckBox}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.2rem", // Ajuste do tamanho do ícone
                      },
                    }}
                  />
                }
                label={t("permanent-approval", {
                  ns: "manual-validation-modal",
                })}
                sx={{
                  ".MuiFormControlLabel-label": {
                    color: "#000000de", // Use sua cor personalizada
                    fontWeight: "400",
                    fontSize: "0.9rem",
                  },
                }}
              />
            )}
            <Typography
              variant="overline"
              color="textSecondary"
              style={{ marginTop: "16px" }}
            >
              {t("validation-reason", { ns: "manual-validation-modal" })}
            </Typography>
            <TextField
              disabled={isSubmitting}
              variant="outlined"
              className="cy-reason-text-field"
              value={observation}
              multiline
              rows={4}
              onChange={(e) => setObservation(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <ButtonTS
          className="cy-cancel-button"
          color="transparent"
          disabled={isSubmitting}
          onClick={() => setOpen(false)}
        >
          {t("cancel", { ns: "manual-validation-modal" })}
        </ButtonTS>
        <ButtonTS
          className="cy-submit-button"
          color="primary"
          disabled={isSubmitting}
          onClick={() => onSubmit()}
        >
          {t("send", { ns: "manual-validation-modal" })}
        </ButtonTS>
      </DialogActions>
    </Dialog>
  );
}
